import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
    Suits,
    HonorCardsNoTens,
    DealSeats, CardSeats, 
    TableSeats, AuctionSeats,
    asDealSeats, asCardSeats,
} from './BridgeTypes';
import {
    BoardJson,
    GameSettingsContext, GameSettingsState, 
    AuctionNotes, 
    SkillLevel
} from './BridgeEngineSettings';
import { Cards, DeckStateContext, DeckStateContextValue } from './BridgeEngineDeck';

// PlayStateContext.
export enum TurnMode {
    Initializing = 'initializing',
    Playing = 'playing',
    Reviewing = 'reviewing',
    Finished = 'finished',
}

export enum TurnOutcome {
    Win = 'win',
    Lose = 'lose',
    Next = 'next',
}

// MatchScore.
export enum Matches {
    All = 'all',
    Rank = 'rank',
    Suit = 'suit',
}

export type MatchScore = { [match in Matches]: number };
export type SeatsMatchScore = { [seat in DealSeats]: MatchScore };
export function Match0(): SeatsMatchScore {
    return {
        north: { all: 0, rank: 0, suit: 0 },
        south: { all: 0, rank: 0, suit: 0 },
        east: { all: 0, rank: 0, suit: 0 },
        west: { all: 0, rank: 0, suit: 0 },
    };
};

export interface TurnState {
    playMode: TurnMode;
    turnOutcome: TurnOutcome;
    guessCount: number;
    reviewTurn: number;
    leadShown: boolean;
    hintsShown: number;
    matchScores: SeatsMatchScore;
}

const initialTurnState: TurnState = {
    playMode: TurnMode.Initializing,
    turnOutcome: TurnOutcome.Next,
    guessCount: 1,
    reviewTurn: 0,
    leadShown: false,
    hintsShown: 0,
    matchScores: Match0(),
};

export type Deal = { [seat in DealSeats]: string[] };
export interface BoardState {
    playerSeat: DealSeats;
    boardId: string;
    dealerSeat: DealSeats;
    vulnerableSeats: DealSeats[];
    deal: Deal;
    auction: string[];
    auctionNotes: AuctionNotes[];
    lead: string;
    hints: string[][];
}

const initialBoardState: BoardState = {
    boardId: '',
    playerSeat: DealSeats.West,
    dealerSeat: DealSeats.West,
    vulnerableSeats: [],
    deal: {
        "north": ['As', 'Ks', 'Qs', 'Js', 'Tc', '9c', '8c', '7d', '6d', '5d', '4h', '3h', '2h'],
        "south": ['Ah', 'Kh', 'Qh', 'Jh', 'Ts', '9s', '8s', '7c', '6c', '5c', '4d', '3d', '2d'],
        "east": ['Ad', 'Kd', 'Qd', 'Jd', 'Th', '9h', '8h', '7s', '6s', '5s', '4c', '3c', '2c'],
        "west": ['Ac', 'Kc', 'Qc', 'Jc', 'Td', '9d', '8d', '7h', '6h', '5h', '4s', '3s', '2s']
    },
    auction: ['P', 'P', 'P', 'P'],
    auctionNotes: [],
    lead: '',
    hints: [],
};

export interface PlayStateContextValue {
    turnState: TurnState;
    boardState: BoardState;

    newGame: () => void;
    nextGuess: () => void;
    turnReview: (guessCount: number) => void;
    doneReviewing: () => void;
    giveUp: () => void;
    showLead: () => void;
    getLeadSeat: () => DealSeats | null;
    showHint: () => void;
    isRandomCardAvailable: () => boolean;
    showRandomCard: () => void;
    showAnswer: () => void;
}

// initial state.
const initialPlayStateContextValue: PlayStateContextValue = {
    turnState: initialTurnState,
    boardState: initialBoardState,

    newGame: (): void => { },
    nextGuess: (): void => { },
    turnReview: (guessCount: number): void => { },
    doneReviewing: (): void => { },
    giveUp: (): void => { },
    showLead: (): void => { },
    getLeadSeat: (): DealSeats | null => { return DealSeats.North; },
    showHint: (): void => { },
    isRandomCardAvailable: (): boolean => { return false; },
    showRandomCard: (): void => { },
    showAnswer: (): void => { },
};
export const PlayStateContext = createContext<PlayStateContextValue>(initialPlayStateContextValue);
export const PlayStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [turnState, setTurnState] = useState<TurnState>(initialTurnState);
    const [boardState, setBoardState] = useState<BoardState>(initialBoardState);

    const deckContext: DeckStateContextValue = useContext(DeckStateContext)!;

    const gameSettingsStateContext = useContext(GameSettingsContext)!;
    const { gameSettings, updateGameSettings, transformDealJsonToDeal } = gameSettingsStateContext;

    const [preReviewMatchScores, setPreReviewMatchScores] = useState<SeatsMatchScore>(Match0());

    // initial load new game.
    useEffect(() => {
        if (boardState.boardId === '' && gameSettings.allBoards.length > 0) {
            newGame();
        }
    }, [gameSettings.allBoards, boardState.boardId]);

    useEffect(() => {
        computeMatchScores();
    }, [turnState.guessCount]);
    
    useEffect(() => {
        calculateWin();
    }, [turnState.matchScores]);

    useEffect(() => {
        if (turnState.playMode === TurnMode.Reviewing || turnState.playMode === TurnMode.Finished) {
            computeMatchScores();
        }
    }, [deckContext.deck]);

    const updateTurnState = (newTurnState: Partial<TurnState>): void => {
        setTurnState((prevTurnState) => ({ ...prevTurnState, ...newTurnState }));
    }

    const updateBoardState = (newBoardState: Partial<BoardState>): void => {
        setBoardState((prevBoardState) => ({ ...prevBoardState, ...newBoardState }));
    }

    //TODO: add firstGame to capture startup and loading state from browser.
    const newGame = (): void => {
        let randomBoardIndex: number | null = null;
        if (gameSettings.oneshotBoard) {
            const oneshotBoardId = gameSettings.oneshotBoard.toUpperCase();
            randomBoardIndex = gameSettings.allBoards.findIndex(board => board.boardId.toUpperCase() === oneshotBoardId);
            updateGameSettings({ oneshotBoard: null });
        } 
        // must test against null because 0 is a valid index.
        if (randomBoardIndex === null) {
            // pick a random game, but not the same as last time.
            const lastGameIndex = gameSettings.allBoards.findIndex(board => board.boardId === boardState.boardId);
            do {
                randomBoardIndex = Math.floor(Math.random() * gameSettings.allBoards.length);
            } while (randomBoardIndex === lastGameIndex);
        }
        // testing.
        // randomBoardIndex = lastGameIndex + 1; // first game.
        // if (randomBoardIndex >= allBoards.length) {
            //  randomBoardIndex = 0;
        // }

        let randomSeatIndex: number | null = null;
        if (gameSettings.fixedPlayerSeat) {
            randomSeatIndex = TableSeats.findIndex(seat => seat === gameSettings.fixedPlayerSeat);
        } 
        if (gameSettings.oneshotPlayerSeat) {
            randomSeatIndex = TableSeats.findIndex(seat => seat === gameSettings.oneshotPlayerSeat);
            updateGameSettings({ oneshotPlayerSeat: null });
        }
        // must test against null because 0 is a valid index.
        if (randomSeatIndex === null) {
            // pick a random seat, but not the same as last time.
            const lastSeatIndex = TableSeats.findIndex(seat => seat === boardState.playerSeat);
            do {
                randomSeatIndex = Math.floor(Math.random() * TableSeats.length);
            } while (randomSeatIndex === lastSeatIndex);
        }
        // testing.
        // randomSeatIndex = lastSeatIndex + 1;
        // if (randomSeatIndex >= TableSeats.length) {
        //     randomSeatIndex = 0;
        // }

        const randomBoard: BoardJson = gameSettings.allBoards[randomBoardIndex];
        if (randomBoard) {
            updateTurnState({
                    playMode: TurnMode.Playing,
                    turnOutcome: TurnOutcome.Next,
                    guessCount: 1,
                    reviewTurn: 0,
                    leadShown: false,
                    hintsShown: 0,
                    matchScores: Match0(),
            });
            const newDeal: Deal = transformDealJsonToDeal(randomBoard.deal);
            const newPlayerSeat: DealSeats = TableSeats[randomSeatIndex];
            updateBoardState({
                boardId: randomBoard.boardId,
                playerSeat: newPlayerSeat,
                dealerSeat: randomBoard.dealerSeat as DealSeats,
                vulnerableSeats: randomBoard.vulnerableSeats as DealSeats[],
                deal: newDeal,
                auction: randomBoard.auction,
                auctionNotes: randomBoard.auctionNotes ?? [],
                lead: randomBoard.lead ?? '',
                hints: randomBoard.hints ?? [],
            });
            updateDealtDeck(newDeal, newPlayerSeat);
        }
    }

    const updateDealtDeck = (newDeal: Deal, playerSeat: DealSeats) => {
        Object.values(newDeal).forEach((seat, index) => {
            seat.forEach((card) => {
                if (TableSeats[index] === playerSeat) {
                    deckContext.updateCardSeat(card, asCardSeats(playerSeat));
                } else {
                    deckContext.updateCardSeat(card, CardSeats.Off);
                }
                deckContext.updateCardRevealed(card, false);
            });
        });
    }

    //TODO: most of this should be a useEffect.
    const nextGuess = (): void => {
        // save the deck for the current turn.
        deckContext.saveDeck(turnState.guessCount);
        
        // bump turn if not finished.
        setTurnState((prevTurnState) => ({
            ...prevTurnState,
            guessCount: prevTurnState.playMode === TurnMode.Finished ? prevTurnState.guessCount : prevTurnState.guessCount + 1,
        }));
    }

    const calculateWin = (): void => {
        // sums all matchAll scores.
        const totalMatchAll = TableSeats.reduce((total, seat) => {
            if (seat === boardState.playerSeat) return total;
            return total + turnState.matchScores[seat]['all'];
        }, 0);
        
        // calculates the number of honors cards in the playerSeat's hand.
        const playerHand = boardState.deal[boardState.playerSeat];
        const playerHonorCardCount = playerHand.reduce((count, card) => {
            if (HonorCardsNoTens.includes(card[0])) {
                return count + 1;
            }
            return count;
        }, 0);

        // calculates the number of 10s in the playerSeat's hand.
        const playerTensCount = playerHand.reduce((count, card) => {
            if (card[0] === 'T') {
                return count + 1;
            }
            return count;
        }, 0);

        // WIN/LOSS logic.
        // check for LOSS.
        if (turnState.guessCount >= gameSettings.maxTurns) {
            updateTurnState({ turnOutcome: TurnOutcome.Lose, playMode: TurnMode.Finished });
            showAnswer();
        // check for WIN for honor cards only and honor cards + 10s only.
        } else if (gameSettings.honorsOnly) {
                // if we matched cards A, K, Q, & J (4) in all non-player seats (4), we win.
                if (!gameSettings.tenIsHonor && ((totalMatchAll + playerHonorCardCount) === (4 * 4))) {
                    updateTurnState({ turnOutcome: TurnOutcome.Win, playMode: TurnMode.Finished });
                // if we matched cards A, K, Q, J & T (5) in all non-player seats (3), we win.
                } else if (gameSettings.tenIsHonor && ((totalMatchAll + playerHonorCardCount + playerTensCount) === (5 * 4))) {
                    updateTurnState({ turnOutcome: TurnOutcome.Win, playMode: TurnMode.Finished });
                }
            } 
        // check for WIN for all cards play.
        else if (totalMatchAll === (13 * 3)) {
            updateTurnState({ turnOutcome: TurnOutcome.Win, playMode: TurnMode.Finished });
        } 
        
        return;
    }

    const turnReview = (reviewTurn: number): void => {
        if (turnState.playMode === TurnMode.Playing) {
            // deep copy.
            // setPreReviewMatchScores(Object.fromEntries(
            //     Object.entries(matchScores).map(([seat, matchScore]) => [seat,{ ...matchScore }])
            // ) as SeatsMatchScore);
            // we're setting everything, so no previsous state needed.
            setPreReviewMatchScores({
                north: { ...turnState.matchScores.north },
                south: { ...turnState.matchScores.south },
                east: { ...turnState.matchScores.east },
                west: { ...turnState.matchScores.west },
            });
            deckContext.saveDeck(turnState.guessCount);
        }
        deckContext.restoreDeck(reviewTurn);
        computeMatchScores();
        if (turnState.playMode !== TurnMode.Finished) {
            updateTurnState({playMode: TurnMode.Reviewing, reviewTurn: reviewTurn});
        }
    }

    const doneReviewing = (): void => {
        if (turnState.playMode === TurnMode.Playing) {
            return;
        }
        // deep copy.
        // setMatchScores(Object.fromEntries(
        //     Object.entries(preReviewMatchScores).map(([seat, matchScore]) => [seat, { ...matchScore }])
        // ) as SeatsMatchScore);
        updateTurnState({
            matchScores: {
                north: { ...preReviewMatchScores.north },
                south: { ...preReviewMatchScores.south },
                east: { ...preReviewMatchScores.east },
                west: { ...preReviewMatchScores.west },
            }
        });
        deckContext.restoreDeck(turnState.guessCount);
        if (turnState.playMode !== TurnMode.Finished) {
            updateTurnState({playMode: TurnMode.Playing, reviewTurn: 0});
        }
    }
    
    const giveUp = (): void => {
        showAnswer();
        updateTurnState({ turnOutcome: TurnOutcome.Lose, playMode: TurnMode.Finished });
        // update the match scores, etc.
        nextGuess();
    }

    const getLeadSeat = (): DealSeats | null => {
        if (boardState.auction.length === 0) {
            return null;
        }        
        const reversedAuction = [...boardState.auction].reverse();
        // find the winning bid.
        let winningBid: string = '';
        for (const bid of reversedAuction) {
            if (bid.length > 0 && !isNaN(Number(bid[0]))) {
                winningBid = bid;
                break;
            }
        }
        if (winningBid === '') {
            return null;
        }

        // find the declarer.
        const winningBidIndex = reversedAuction.findIndex(bid => bid === winningBid);
        let declarerSeatIndex: number = winningBidIndex;
        // start from the winning bid and move back two seats at a time to find the declarer.
        for (let i = winningBidIndex; i < reversedAuction.length; i = i + 2) {
            const bid = reversedAuction[i];
            if (bid.length > 1 && bid[1] === winningBid[1]) {
                declarerSeatIndex = i;
            }
        }

        const dealerIndex = AuctionSeats.indexOf(boardState.dealerSeat);
        const declarerSeat: DealSeats = AuctionSeats[(boardState.auction.length + dealerIndex - (declarerSeatIndex)) % 4] as DealSeats;
        return declarerSeat;
    }
    
    const showLead = (): void => {
        updateTurnState({ leadShown: true });
    }

    const showHint = (): void => {
        setTurnState((prevTurnState) => ({ ...prevTurnState, hintsShown: prevTurnState.hintsShown + 1 }));
    }

    const showRandomCard = (): void => {
        const unrevealedCards = getUnrevealedCards();
        if (unrevealedCards.length === 0) {
            return;
        }

        // pick a random card from the array and set it's seat and that it's been revealed.
        const randomCard = unrevealedCards[Math.floor(Math.random() * unrevealedCards.length)];
        const dealSeat: DealSeats | undefined = (Object.keys(boardState.deal) as (keyof Deal)[]).
                                                    find((seat) => boardState.deal[seat].includes(randomCard.id));
        if (dealSeat !== undefined) {
            deckContext.updateCardSeat(randomCard.id, asCardSeats(dealSeat));
            deckContext.updateCardRevealed(randomCard.id, true);
        }
    }

    const isRandomCardAvailable = (): boolean => {
        return getUnrevealedCards().length > 0;
    }

    const getUnrevealedCards = (): Cards[] => {
        let unrevealedCards = deckContext.deck.filter(deckCard => deckCard.revealed === false);

        // remove cards in the playerSeat's hand.
        unrevealedCards = unrevealedCards.filter(deckCard => {
            return !boardState.deal[boardState.playerSeat].includes(deckCard.id);
        });

        // the reduce the array based on the skill level settings.
        if (gameSettings.honorsOnly) {
            unrevealedCards = unrevealedCards.filter(deckCard =>
                HonorCardsNoTens.includes(deckCard.rank) || deckCard.rank === 'T');
            if (!gameSettings.tenIsHonor) {
                unrevealedCards = unrevealedCards.filter(deckCard => deckCard.rank !== 'T');
            }
        }

        return unrevealedCards;
    }
    const showAnswer = (): void => {
        // assign all the cards in the deck to the seat in the deal.
        Object.entries(boardState.deal).forEach(([seat, cards]) => {
            cards.forEach((card) => {
                deckContext.updateCardSeat(card, seat as CardSeats);
            });
        });
    }

    // compute match score.
    const computeMatchScores = (): void => {
        let newMatchScores: SeatsMatchScore = Match0();
        TableSeats.forEach((seat) => {
            if (seat === boardState.playerSeat) {
                return;
            }
            newMatchScores[seat] = computeSeatMatchScore(seat);
        });
        updateTurnState({ matchScores: newMatchScores });
    }
    
    const computeSeatMatchScore = (matchSeat: DealSeats): MatchScore => {
        let matchAllCount: number = 0;
        let matchRankCount: number = 0;
        let matchSuitCount: number = 0;
        let matchedCards: string[] = [];

        let usedDeal = boardState.deal[matchSeat].map(card => card);
        // determine all exact matches.
        deckContext.deck.forEach((deckCard) => {
            if (deckCard.seat === asCardSeats(matchSeat)) {
                let cardIndex: number = usedDeal.indexOf(deckCard.id);
                if (cardIndex !== -1) {
                    matchAllCount++;
                    // keep track of which cards to skip in the partial match.
                    matchedCards.push(deckCard.id);
                    // don't let suit or rank match on this.
                    usedDeal[cardIndex] = '  ';

                    // reveal the matched card.
                    if (gameSettings.guessMarksCorrect) {
                        deckContext.updateCardRevealed(deckCard.id, true);
                    }
                }
            }
        });

        // determine all partial matches.
        deckContext.deck.forEach((deckCard) => {
            if (deckCard.seat ===asCardSeats(matchSeat)) {
                
                // was the card in the original deal?
                if (matchedCards.includes(deckCard.id)) {
                    return;
                }
                
                // match rank.
                const rank = deckCard.rank;
                let cardIndex: number = usedDeal.findIndex((dealCard) => dealCard[0] === rank);
                if (cardIndex !== -1) {
                    matchRankCount++;
                    usedDeal[cardIndex] = ' ' + usedDeal[cardIndex][1];
                }
                
                // match suit.
                const suit = deckCard.suit;
                cardIndex = usedDeal.findIndex((dealCard) => dealCard[1] === suit);
                if (cardIndex !== -1) {
                    matchSuitCount++;
                    usedDeal[cardIndex] = usedDeal[cardIndex][0] + ' ';
                }
            }
        });

        return { all: matchAllCount, rank: matchRankCount, suit: matchSuitCount };
    }
 
    return (
        <PlayStateContext.Provider value={{
            turnState: turnState,
            boardState: boardState,

            newGame,
            turnReview,
            nextGuess,
            doneReviewing,
            giveUp,
            showLead,
            getLeadSeat,
            showHint,
            isRandomCardAvailable,
            showRandomCard,
            showAnswer,
        }}>
            {children}
        </PlayStateContext.Provider>
    );
};

export default PlayStateProvider;
